/**
 * Le LaTeX restreint en MathML — pour le document imprimable d'une copie.
 *
 * Pourquoi MathML : WKWebView (iOS, `expo-print`) et Chrome l'affichent sans
 * script ni police à charger. Un document autonome, imprimable en PDF, ne peut
 * compter sur rien d'autre.
 *
 * On ne relit PAS le LaTeX une seconde fois : on part de l'arbre que dessine
 * déjà l'application (`analyserTexteLatex`, `../math/latex.ts`). La liste
 * fermée des commandes est donc la même, et un segment que l'app refuse de
 * dessiner est refusé ici aussi — il retombe en texte, sans ses délimiteurs,
 * dans une police à chasse fixe. Mieux vaut le texte brut qu'un rendu qui
 * affirme autre chose que ce qui est écrit.
 *
 * Tout le texte est échappé : aucun contenu de copie ne devient du HTML.
 */
import { analyserTexteLatex } from '../math/latex.js';
import { replierLatex, replierProse } from '../math/repli.js';
const ENTITES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
/** Échappe un texte pour le poser dans du HTML ou du MathML, en contenu comme en attribut. */
export function echapperHtml(texte) {
    return texte.replace(/[&<>"']/g, (c) => ENTITES[c] ?? c);
}
const ESPACE_INSECABLE = '\u00A0';
/** Les préfixes qui nomment une figure : `∠ABC` est un nom, pas un mot. */
const PREFIXES_NOMS = ['∠', '△'];
function atomeVersMathMl(texte) {
    if (texte === ESPACE_INSECABLE)
        return '<mspace width="0.25em"/>';
    if (texte === '')
        return '<mrow/>';
    const t = echapperHtml(texte);
    /* Un nombre, signé ou non : l'analyseur colle le signe au nombre (`5^{-3}`). */
    if (/^[−+±-]?[0-9]/.test(texte))
        return `<mn>${t}</mn>`;
    const lettres = [...texte.replace(/[\u0300-\u036f\u20d0-\u20ff]/g, '')];
    if (lettres.length === 1 || PREFIXES_NOMS.some((p) => texte.startsWith(p))) {
        return `<mi>${t}</mi>`;
    }
    return `<mtext>${t}</mtext>`;
}
const DELIMITEURS = {
    parentheses: ['(', ')'],
    crochets: ['[', ']'],
    accolades: ['{', '}'],
};
const GRANDS_OPERATEURS = {
    somme: '∑',
    produit: '∏',
    integrale: '∫',
};
/** Un enfant d'un élément à arité fixe (`mfrac`, `msup`…) : toujours UN nœud. */
function enRangee(n) {
    const m = noeudVersMathMl(n);
    return n.type === 'rangee' ? m : `<mrow>${m}</mrow>`;
}
function barriere(c) {
    return `<mo fence="true">${echapperHtml(c)}</mo>`;
}
/** Un nœud de l'arbre partagé (`expression.ts`) en MathML Core. */
export function noeudVersMathMl(n) {
    switch (n.type) {
        case 'atome':
            return atomeVersMathMl(n.texte);
        case 'operateur':
            return `<mo>${echapperHtml(n.texte)}</mo>`;
        case 'rangee':
            return `<mrow>${n.enfants.map(noeudVersMathMl).join('')}</mrow>`;
        case 'fraction':
            return `<mfrac>${enRangee(n.numerateur)}${enRangee(n.denominateur)}</mfrac>`;
        case 'script': {
            const base = enRangee(n.base);
            if (n.exposant !== undefined && n.indice !== undefined) {
                return `<msubsup>${base}${enRangee(n.indice)}${enRangee(n.exposant)}</msubsup>`;
            }
            if (n.exposant !== undefined)
                return `<msup>${base}${enRangee(n.exposant)}</msup>`;
            if (n.indice !== undefined)
                return `<msub>${base}${enRangee(n.indice)}</msub>`;
            return base;
        }
        case 'racine':
            return n.degre === undefined
                ? `<msqrt>${noeudVersMathMl(n.radicande)}</msqrt>`
                : `<mroot>${enRangee(n.radicande)}${enRangee(n.degre)}</mroot>`;
        case 'valeurAbsolue':
            return `<mrow>${barriere('|')}${noeudVersMathMl(n.contenu)}${barriere('|')}</mrow>`;
        case 'norme':
            return `<mrow>${barriere('‖')}${noeudVersMathMl(n.contenu)}${barriere('‖')}</mrow>`;
        /* `<mover>` accepte un contenu de plusieurs caractères et étire son
           ornement : c'est exactement ce qu'aucun caractère combinant ne sait
           faire, et la raison d'être du nœud « surmonte ». */
        case 'surmonte':
            return `<mover accent="true">${enRangee(n.contenu)}<mo stretchy="true">${n.ornement === 'barre' ? '&#x203E;' : '&#x2192;'}</mo></mover>`;
        case 'groupe': {
            const [ouvrant, fermant] = DELIMITEURS[n.delimiteur] ?? ['(', ')'];
            return `<mrow>${barriere(ouvrant)}${noeudVersMathMl(n.contenu)}${barriere(fermant)}</mrow>`;
        }
        case 'systeme': {
            const lignes = n.lignes.map((l) => `<mtr><mtd>${noeudVersMathMl(l)}</mtd></mtr>`).join('');
            return `<mrow>${barriere('{')}<mtable columnalign="left">${lignes}</mtable></mrow>`;
        }
        case 'grandOperateur': {
            const symbole = `<mo largeop="true">${GRANDS_OPERATEURS[n.operateur] ?? '∑'}</mo>`;
            const inf = n.borneInf === undefined ? '<mrow/>' : enRangee(n.borneInf);
            const sup = n.borneSup === undefined ? '<mrow/>' : enRangee(n.borneSup);
            const bornes = n.borneInf === undefined && n.borneSup === undefined
                ? symbole
                : n.operateur === 'integrale'
                    ? `<msubsup>${symbole}${inf}${sup}</msubsup>`
                    : `<munderover>${symbole}${inf}${sup}</munderover>`;
            return `<mrow>${bornes}${noeudVersMathMl(n.corps)}</mrow>`;
        }
        case 'cible':
            return noeudVersMathMl(n.contenu);
        case 'sousAccolade':
            return `<munder><munder>${enRangee(n.contenu)}<mo stretchy="true">&#x23DF;</mo></munder>${enRangee(n.legende)}</munder>`;
        case 'matrice': {
            const [ouvrant, fermant] = n.delimiteur === 'crochets' ? ['[', ']'] : ['(', ')'];
            const lignes = n.lignes
                .map((l) => `<mtr>${l.map((c) => `<mtd>${noeudVersMathMl(c)}</mtd>`).join('')}</mtr>`)
                .join('');
            return `<mrow>${barriere(ouvrant)}<mtable>${lignes}</mtable>${barriere(fermant)}</mrow>`;
        }
    }
}
function enveloppe(n) {
    return `<math xmlns="http://www.w3.org/1998/Math/MathML" display="inline">${enRangee(n)}</math>`;
}
/**
 * Une expression en LaTeX restreint — le contenu d'un `\( … \)`, délimiteurs
 * compris ou non — en un élément `<math>`. `null` quand l'app ne la
 * dessinerait pas (commande hors liste, accolade déséquilibrée…).
 */
export function latexVersMathMl(latex) {
    const t = latex.trim();
    const source = t.startsWith('\\(') && t.endsWith('\\)') ? t : `\\(${t}\\)`;
    const segments = analyserTexteLatex(source);
    const seul = segments[0];
    if (segments.length !== 1 || seul?.type !== 'expression')
        return null;
    return enveloppe(seul.noeud);
}
/**
 * Un segment refusé, rendu à la prose : son contenu REPLIÉ (`replierLatex`),
 * sans `\(` ni `\)`, en chasse fixe. Le document imprimé dégrade donc
 * exactement comme l'app — et, comme elle, il ne montre jamais de barre
 * oblique inverse à un élève ni à son parent.
 */
function proseAvecSegmentsBruts(prose) {
    const morceaux = [];
    const motif = /\\\(([\s\S]*?)(\\\)|$)/g;
    let curseur = 0;
    for (let m = motif.exec(prose); m !== null; m = motif.exec(prose)) {
        if (m.index > 0 && prose.charAt(m.index - 1) === '\\')
            continue;
        morceaux.push(echapperHtml(replierProse(prose.slice(curseur, m.index))));
        morceaux.push(`<span class="tex">${echapperHtml(replierLatex((m[1] ?? '').trim()))}</span>`);
        curseur = m.index + m[0].length;
        if (m[0].length === 0)
            motif.lastIndex += 1;
    }
    morceaux.push(echapperHtml(replierProse(prose.slice(curseur))));
    return morceaux.join('');
}
/**
 * Un texte de copie en HTML : la prose échappée, chaque `\( … \)` en MathML.
 * Le français s'écrit sans mathématiques (comme à l'écran) : tout y est prose.
 */
export function texteVersHtml(texte, maths) {
    if (!maths)
        return echapperHtml(texte);
    return analyserTexteLatex(texte)
        .map((s) => (s.type === 'expression' ? enveloppe(s.noeud) : proseAvecSegmentsBruts(s.texte)))
        .join('');
}
//# sourceMappingURL=mathml.js.map